import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';
import { brandAds } from '@/config/ads';
import { useScript } from '@/hooks/use-script';
import { useAdUrl } from '@/hooks/use-consent';


interface HeroSectionProps {
  cmsData?: Record<string, unknown>;
}

const HeroSection: React.FC<HeroSectionProps> = ({ cmsData }) => {
  const navigate = useNavigate();
  const adContainerRef = useRef<HTMLDivElement>(null);

  // Resolved against the visitor's consent signal; empty until Zaraz reports consent state
  const heroAdUrl = useAdUrl((cmsData?.ad_url as string) || brandAds.ad1);
  const { ready } = useScript({
    src: heroAdUrl ?? '',
    appendTo: adContainerRef,
    id: 'hero-ad-script',
  });

  return (
    <section className="relative min-h-screen flex items-center pt-20 lg:pt-0 overflow-hidden">
      {/* Stripe-style gradient background */}
      <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
        {/* Grid pattern */}
        <div className="absolute inset-0 grid-pattern" />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] opacity-30 dark:opacity-40">
          <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-[#635bff] via-[#00d4ff] to-transparent rounded-full blur-3xl animate-float" />
        </div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] opacity-20 dark:opacity-30">
          <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-tr from-[#ff80b5] via-[#635bff] to-transparent rounded-full blur-3xl animate-float-delayed" />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] opacity-10 dark:opacity-20">
          <div className="w-full h-full bg-gradient-to-r from-[#00d4ff] to-[#635bff] rounded-full blur-3xl animate-float-slow" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="max-w-xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.05]">
              <span className="text-slate-900 dark:text-white">{(cmsData?.headline_line1 as string) || 'Static ads are '}</span>
              <span className="stripe-gradient-text">{(cmsData?.headline_gradient as string) || 'dying.'}</span>
              <br />
              <span className="text-slate-900 dark:text-white">{(cmsData?.headline_line2 as string) || "Your revenue doesn't have to."}</span>
            </h1>

            <p className="mt-8 text-lg lg:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
              {(cmsData?.subheadline as string) || 'Create high-impact interactive ads that drive higher engagement and unlock revenue—without the complexity.'}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/demo')}
                className="stripe-button inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white rounded-full group"
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/gallery')}
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-slate-700 dark:text-slate-200 bg-white/80 dark:bg-white/10 backdrop-blur-sm border border-slate-200 dark:border-white/20 rounded-full hover:bg-white dark:hover:bg-white/20 hover:border-slate-300 dark:hover:border-white/30 transition-all group"
              >
                <Play className="mr-2 h-5 w-5" />
                View Ad Gallery
              </button>
            </div>
          </div>

          {/* Right Content - Interactive Ad Showcase */}
          <div className="relative lg:pl-8">
            {/* Glow effect behind card */}
            <div className="absolute -inset-4 bg-gradient-to-r from-[#635bff]/20 via-[#00d4ff]/20 to-[#ff80b5]/20 rounded-3xl blur-2xl opacity-60 dark:opacity-40" />
            
            {/* Main Ad Display */}
            <div className="relative">
              <div
                ref={adContainerRef}
                className={`relative embedded-ad-container aspect-[4/3] bg-white dark:bg-slate-800/50 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 overflow-hidden border border-slate-200/50 dark:border-white/10 backdrop-blur-xl sm:max-w-3/4 mx-auto transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`}
              >
                {/* Ad script is loaded via useScript hook into this container */}
              </div>
              {/* Floating badge */}
              {/* <div className="absolute top-4 right-4 glass-card px-3 py-1.5 rounded-full z-10">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Live Ad Preview</span>
              </div> */}
            </div>

            

            {/* Floating Stats Cards */}
            {/* <div className="absolute -left-8 top-1/4 glass-card rounded-xl shadow-xl p-4 hidden lg:block animate-float">
              <div className="text-2xl font-bold stripe-gradient-text">3x</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Higher CTR</div>
            </div>
            <div className="absolute -right-8 bottom-1/4 glass-card rounded-xl shadow-xl p-4 hidden lg:block animate-float-delayed">
              <div className="text-2xl font-bold text-emerald-500">+47%</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Engagement</div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
        <span className="text-xs font-medium">Scroll to explore</span>
        <div className="w-6 h-10 border-2 border-slate-300 dark:border-slate-600 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#635bff] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
